import { PreUploadContent } from '@components/dropzone/PreUploadContent'
import type { DropzoneFile } from '@components/dropzone/types'
import { useUniqueId } from '@hooks/useUniqueId'
import { lighten } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Tooltip from '@mui/material/Tooltip'
import { useSnackbar } from 'notistack'
import type { ChangeEventHandler, DragEventHandler, ForwardedRef, DragEvent } from 'react'
import { useCallback, useState, useRef } from 'react'
import type { ControllerRenderProps, FieldValues, Path } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

export type Props<T extends FieldValues> = {
  label: string
  supportedFileTypesLabel: string
  field: ControllerRenderProps<T, Path<T>>
  ref?: ForwardedRef<HTMLDivElement>
  disabled?: boolean
  disabledTooltipText?: string
  renderPostUploadContent?: (file: DropzoneFile, index: number) => JSX.Element
  multiple?: boolean
}

export const Dropzone = <T extends FieldValues>({
  label,
  supportedFileTypesLabel,
  field,
  disabled,
  ref,
  disabledTooltipText,
  renderPostUploadContent,
  multiple = false,
}: Props<T>) => {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const { enqueueSnackbar } = useSnackbar()
  const [isDragging, setIsDragging] = useState(false)
  const { generateId } = useUniqueId('dropzone')

  const onChange = useCallback(
    (files: Array<File | DropzoneFile>) => {
      if (disabled) {
        return
      }

      const newFiles: Array<DropzoneFile> = files.map((file) => {
        //check if file is already a DropzoneFile
        if ('id' in file) {
          return file
        }
        //create DropzoneFile
        return { id: generateId(), file }
      })

      //validation
      if (newFiles.length === 0) {
        return
      }

      if (
        newFiles.some(
          (dropzoneFile) => !supportedFileTypesLabel.includes(dropzoneFile?.file.name.split('.').pop() || '')
        )
      ) {
        enqueueSnackbar(t('dropzone.error_file_type'), { variant: 'error' })
        return
      }

      //change
      field.onChange(newFiles)
    },
    [disabled, enqueueSnackbar, field, generateId, t]
  )

  const handleDragEnter = useCallback((event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDrop: DragEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      event.preventDefault()
      event.stopPropagation()
      const newFiles: Array<File> = [...event.dataTransfer.files]
      if (multiple) {
        onChange([...field.value, ...newFiles])
      } else {
        onChange(newFiles)
      }
      setIsDragging(false)
    },
    [field.value, onChange]
  )

  const handleDragLeave = useCallback((event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    const rect = event.currentTarget.getBoundingClientRect()
    if (
      event.clientX < rect.left ||
      event.clientX >= rect.right ||
      event.clientY < rect.top ||
      event.clientY >= rect.bottom
    ) {
      setIsDragging(false)
    }
  }, [])

  const handleDragOver: DragEventHandler<HTMLDivElement> = useCallback((event) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleOnChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const newFiles: Array<File> = [...(event.target?.files || [])]
      onChange(newFiles)
    },
    [onChange]
  )

  return (
    <Paper
      ref={ref}
      variant="outlined"
      sx={{
        height: '100%',
        p: 2,
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark'
            ? lighten(theme.palette.primary.light, isDragging ? 0.2 : 0.3)
            : lighten(theme.palette.primary.light, isDragging ? 0.6 : 0.8),
        borderStyle: 'dashed',
        borderColor: (theme) =>
          theme.palette.mode === 'dark'
            ? lighten(theme.palette.primary.dark, isDragging ? 0.1 : 0.2)
            : lighten(theme.palette.primary.dark, isDragging ? 0.4 : 0.6),
        borderWidth: 2,
        transition: 'all 0.5s ease',
      }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Box>
        <Tooltip title={disabled ? disabledTooltipText : null}>
          <Box>
            <PreUploadContent
              label={label}
              disabled={disabled}
              image={
                field.value?.length > 0 ? (
                  <Box
                    display="grid"
                    width="100%"
                    justifyContent="center"
                  >
                    {field.value?.map((file: DropzoneFile, index: number) =>
                      renderPostUploadContent ? renderPostUploadContent(file, index) : file?.file?.name
                    )}
                  </Box>
                ) : null
              }
            >
              <input
                ref={inputRef}
                onChange={handleOnChange}
                type="file"
                multiple={multiple}
                accept={supportedFileTypesLabel}
                hidden
                disabled={disabled}
              />
              <Button
                onClick={() => inputRef.current?.click?.()}
                variant="contained"
                disabled={disabled}
              >
                {t('dropzone.button')}
              </Button>
              <Box sx={{ color: (theme) => (disabled ? theme.palette.primary.light : theme.palette.primary.dark) }}>
                {supportedFileTypesLabel}
              </Box>
            </PreUploadContent>
          </Box>
        </Tooltip>
      </Box>
    </Paper>
  )
}
